/**
 * Form controls.
 *
 * TWO NON-NEGOTIABLE RULES ARE BAKED IN HERE, so no call site has to remember them:
 *
 *  1. `autoComplete="off"` on every control by default (CLAUDE.md rule 5). An HR user
 *     creating a record for SOMEBODY ELSE must never have their own saved address, phone
 *     or email offered as a completion — and the browser's completion popup stealing
 *     focus after a single keystroke is precisely the tab-out bug that made this a rule.
 *     `autoComplete` can be overridden per-control, and the sign-in form does exactly
 *     that, because there the fields really do belong to the person typing.
 *
 *  2. `emp_` name prefix. Browsers match saved values by field NAME heuristics, not by
 *     `autocomplete` alone; an input called `email` gets autofilled regardless. The prefix
 *     breaks that matching. `Input` applies it automatically from the `name` prop.
 *
 * `FieldError` never renders a generic string. Call sites pass the specific, actionable
 * message the API or the validator produced.
 */
import { forwardRef, useId, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import { AlertTriangle, Info, Lock } from 'lucide-react';

import { cn } from '@frontend/lib/cn';

const CONTROL_BASE =
  'w-full rounded-lg border border-line-control bg-surface px-2.5 text-row text-content ' +
  'placeholder:text-content-placeholder transition-token ' +
  'hover:border-line-strong focus:border-line-focus ' +
  'disabled:cursor-not-allowed disabled:bg-surface-muted disabled:text-content-disabled ' +
  'read-only:bg-surface-subtle read-only:text-content-secondary ' +
  'aria-[invalid=true]:border-error aria-[invalid=true]:bg-error-bg/40';

export interface FieldShellProps {
  label: ReactNode;
  htmlFor?: string;
  required?: boolean;
  /** Rendered under the control, above any error. */
  hint?: ReactNode;
  error?: string | null;
  /** Shown as a small "why is this like this" note — used by the dependency engine. */
  note?: ReactNode;
  className?: string;
  children: ReactNode;
  fieldKey?: string;
}

export function FieldShell({
  label,
  htmlFor,
  required = false,
  hint,
  error,
  note,
  className,
  children,
  fieldKey,
}: FieldShellProps): JSX.Element {
  return (
    <div className={cn('flex flex-col gap-1', className)} data-testid={fieldKey === undefined ? undefined : `field-${fieldKey}`}>
      <label htmlFor={htmlFor} className="flex items-center gap-1 text-body font-medium text-content-secondary">
        {label}
        {required && (
          <span aria-hidden className="text-error">
            *
          </span>
        )}
        {required && <span className="sr-only">(required)</span>}
      </label>
      {children}
      {note !== undefined && note !== null && (
        <p className="flex items-start gap-1 text-body text-content-secondary">
          <Info aria-hidden className="mt-px h-3 w-3 shrink-0" />
          <span>{note}</span>
        </p>
      )}
      {hint !== undefined && hint !== null && error == null && (
        <p className="text-body text-content-secondary">{hint}</p>
      )}
      <FieldError error={error} fieldKey={fieldKey} />
    </div>
  );
}

export function FieldError({ error, fieldKey }: { error?: string | null; fieldKey?: string }): JSX.Element | null {
  if (error === undefined || error === null || error === '') return null;
  return (
    <p
      role="alert"
      className="flex items-start gap-1 text-body text-error"
      data-testid={fieldKey === undefined ? 'field-error' : `field-error-${fieldKey}`}
    >
      <AlertTriangle aria-hidden className="mt-px h-3 w-3 shrink-0" />
      <span>{error}</span>
    </p>
  );
}

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  invalid?: boolean;
  /** Skip the `emp_` name prefix. Only the sign-in form should do this. */
  rawName?: boolean;
  inputSize?: 'sm' | 'md';
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, invalid = false, rawName = false, inputSize = 'md', name, autoComplete, ...rest },
  ref,
) {
  return (
    <input
      ref={ref}
      name={name === undefined ? undefined : rawName ? name : `emp_${name}`}
      autoComplete={autoComplete ?? 'off'}
      aria-invalid={invalid || undefined}
      className={cn(CONTROL_BASE, inputSize === 'sm' ? 'h-control-sm text-body' : 'h-control-md', className)}
      {...rest}
    />
  );
});

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean;
  rawName?: boolean;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { className, invalid = false, rawName = false, name, autoComplete, rows = 3, ...rest },
  ref,
) {
  return (
    <textarea
      ref={ref}
      rows={rows}
      name={name === undefined ? undefined : rawName ? name : `emp_${name}`}
      autoComplete={autoComplete ?? 'off'}
      aria-invalid={invalid || undefined}
      className={cn(CONTROL_BASE, 'min-h-[72px] resize-y py-1.5 leading-relaxed', className)}
      {...rest}
    />
  );
});

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  invalid?: boolean;
  rawName?: boolean;
  selectSize?: 'sm' | 'md';
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { className, invalid = false, rawName = false, name, autoComplete, children, selectSize = 'md', ...rest },
  ref,
) {
  return (
    <select
      ref={ref}
      name={name === undefined ? undefined : rawName ? name : `emp_${name}`}
      autoComplete={autoComplete ?? 'off'}
      aria-invalid={invalid || undefined}
      className={cn(
        CONTROL_BASE,
        'appearance-none bg-[length:0] pr-7',
        selectSize === 'sm' ? 'h-control-sm text-body' : 'h-control-md',
        className,
      )}
      {...rest}
    >
      {children}
    </select>
  );
});

export function Checkbox({
  label,
  description,
  className,
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & { label: ReactNode; description?: ReactNode }): JSX.Element {
  const id = useId();
  return (
    <div className={cn('flex items-start gap-2', className)}>
      <input
        id={id}
        type="checkbox"
        autoComplete="off"
        className="mt-0.5 h-3.5 w-3.5 shrink-0 cursor-pointer rounded-sm border border-line-control accent-interactive"
        {...rest}
      />
      <label htmlFor={id} className="cursor-pointer select-none text-row text-content">
        {label}
        {description !== undefined && <span className="block text-body text-content-secondary">{description}</span>}
      </label>
    </div>
  );
}

export function Toggle({
  checked,
  onChange,
  label,
  disabled = false,
  'data-testid': testId,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  disabled?: boolean;
  'data-testid'?: string;
}): JSX.Element {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      data-testid={testId}
      onClick={() => onChange(!checked)}
      className={cn(
        'relative inline-flex h-4.5 w-8 shrink-0 items-center rounded-full border transition-token',
        'h-[18px] w-[32px] disabled:cursor-not-allowed disabled:opacity-50',
        checked ? 'border-interactive bg-interactive' : 'border-line-control bg-surface-muted',
      )}
    >
      <span
        aria-hidden
        className={cn(
          'block h-3 w-3 rounded-full bg-surface shadow-xs transition-token',
          checked ? 'translate-x-[16px]' : 'translate-x-[2px]',
        )}
      />
    </button>
  );
}

/**
 * A control bound to a value the caller may not read.
 *
 * The API returns restricted PII as explicit `null` plus a descriptor. Rendering an empty
 * EDITABLE input for that would be a data-loss bug: the user saves the form, the null
 * round-trips, and a stored home address is silently blanked. So the control is disabled,
 * the reason is shown, and the field is excluded from submission by the caller.
 */
export function WithheldField({
  label,
  message,
  requiredPermission,
  fieldKey,
}: {
  label: ReactNode;
  message: string;
  requiredPermission?: readonly string[];
  fieldKey?: string;
}): JSX.Element {
  return (
    <FieldShell label={label} fieldKey={fieldKey}>
      <div
        className="flex min-h-control-md items-center gap-2 rounded-md border border-dashed border-line-control bg-surface-subtle px-2.5 text-row text-content-secondary"
        data-testid={fieldKey === undefined ? 'withheld-field' : `withheld-${fieldKey}`}
      >
        <Lock aria-hidden className="h-3.5 w-3.5 shrink-0" />
        <span className="truncate">Withheld</span>
      </div>
      <p className="text-body text-content-secondary">{message}</p>
      {requiredPermission !== undefined && requiredPermission.length > 0 && (
        <p className="text-body text-content-secondary">
          Unlocked by: <code className="code-ref">{requiredPermission.join(' or ')}</code>
        </p>
      )}
    </FieldShell>
  );
}
